import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { dashboardService, GlobalStats, UserStatsResponse, User, Transaction } from '../../services/dashboardService';
import { getCurrentUserRole, getCurrentUserId } from '../../utils/auth';
import { cn } from '../../utils/cn';
import { ButtonGroup } from 'react-bootstrap';

type SurfaceCardProps = {
  className?: string;
  children: React.ReactNode;
};

const SurfaceCard: React.FC<SurfaceCardProps> = ({ className, children }) => (
  <section className={cn('rounded-[30px] border border-[#d9dde2] bg-[#f5f6f7] p-[30px] shadow-sm', className)}>
    {children}
  </section>
);

type StatTileProps = {
  label: string;
  value: string | number;
};

const StatTile: React.FC<StatTileProps> = ({ label, value }) => (
  <div className="rounded-xl border border-[#e6eaee] bg-[#f0f2f4] p-3 xl:p-5">
    <p className="text-[10px] xl:text-base uppercase   text-slate-400">{label}</p>
    <p className="mt-[3px] text-base font-semibold text-slate-900">{value}</p>
  </div>
);

type MetricAccent = 'blue' | 'lightgray' | 'mediumgray' | 'darkgray';

type MetricStatCardProps = {
  label: string;
  value: string | number;
  accent: MetricAccent;
  volumeIcon?: boolean;
};

const metricAccentStyles: Record<MetricAccent, { leftColor: string; label: string }> = {
  blue: { leftColor: '#2297D1', label: 'text-[#2297D1]' },
  lightgray: { leftColor: '#d1d5db', label: 'text-[#9ca3af]' },
  mediumgray: { leftColor: '#9ca3af', label: 'text-[#6b7280]' },
  darkgray: { leftColor: '#475569', label: 'text-[#475569]' },
};

const MetricStatCard: React.FC<MetricStatCardProps> = ({ label, value, accent, volumeIcon }) => {
  const s = metricAccentStyles[accent];
  return (
    <div
      className="rounded-[20px] bg-white shadow-md overflow-hidden"
    >
      <div className=" border-l py-2 px-[22px] space-y-[9px]"
      style={{ borderLeft: `6px solid ${s.leftColor}` }}
      >
        <p className={cn('text-[11px] xl:text-base  uppercase  ', s.label)}>{label}</p>
      <div className=" flex items-center  ">
        <p className="text-2xl xl:text-[30px] font-semibold leading-tight text-[#313131]">{value}</p>
        {volumeIcon && (
          <span
            className="inline-flex h-7 w-7 ml-[8px] shrink-0 items-center justify-center rounded-full bg-[#2297D1] text-xs font-bold text-white"
            aria-hidden
          >
            T
          </span>
        )}
      </div>
      </div>
    </div>
  );
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();

  const [globalStats, setGlobalStats] = useState<GlobalStats | null>(null);
  const [userStats, setUserStats] = useState<UserStatsResponse | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showDropdown, setShowDropdown] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const userRole = getCurrentUserRole();
  const userId = getCurrentUserId();

  useEffect(() => {
    // Only fetch users for Admin and SuperAgent roles
    if (userRole === 'Admin' || userRole === 'SuperAgent') {
      fetchUsers();
    }
  }, [userRole]);

  useEffect(() => {
    if (userRole === 'Agent') {
      // Agent users can only see their own data
      setSelectedUserId(userId || '');
    } else if (userRole === 'Admin') {
      // Admin users see global stats
      setSelectedUserId('global');
    } else if (userRole === 'SuperAgent') {
      // SuperAgent users see their own data immediately
      setSelectedUserId(userId || '');
    }
  }, [userRole, userId]);

  useEffect(() => {
    // This effect handles user selection after users are loaded
    if (userRole === 'Admin' && users.length > 0) {
      // Admin users can select different users after they're loaded
      // Keep the existing logic for user selection
    }
  }, [users, userRole]);

  useEffect(() => {
    if (selectedUserId) {
      fetchDashboardData(selectedUserId);
    }
  }, [selectedUserId, userRole]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchUsers = async () => {
    try {
      setLoadingUsers(true);
      setError(null);
      const usersData = await dashboardService.getUsers();
      setUsers(usersData);
    } catch (err) {
      setError('Failed to fetch users');
      console.error('Error fetching users:', err);
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchTransactions = async () => {
    try {
      const transactionsData = await dashboardService.getTransactions(5);
      // Sort by timestamp (most recent first) and take the first 5
      const sortedTransactions = transactionsData
        .sort((a, b) => {
          const dateA = new Date(a.timestamp).getTime();
          const dateB = new Date(b.timestamp).getTime();
          // Handle invalid dates by putting them at the end
          if (isNaN(dateA) && isNaN(dateB)) return 0;
          if (isNaN(dateA)) return 1;
          if (isNaN(dateB)) return -1;
          return dateB - dateA; // Most recent first
        })
        .slice(0, 5);
      setTransactions(sortedTransactions);
    } catch (err) {
      console.error('Error fetching transactions:', err);
      // Don't set error state for transactions as it's not critical
    }
  };

  const fetchDashboardData = async (targetUserId: string = selectedUserId) => {
    const isStatsForbidden = (err: unknown): boolean =>
      axios.isAxiosError(err) && err.response?.status === 403;

    try {
      setLoading(true);
      setError(null);

      // Fetch stats for selected scope (403 = role/scope not allowed; continue without stats)
      if (userRole !== 'Agent') {
        if (targetUserId === 'global') {
          try {
            const globalData = await dashboardService.getGlobalStats();
            setGlobalStats(globalData);
            setUserStats(null);
          } catch (err) {
            if (isStatsForbidden(err)) {
              setGlobalStats(null);
              setUserStats(null);
            } else {
              throw err;
            }
          }
        } else if (targetUserId) {
          try {
            const userData = await dashboardService.getUserStats(parseInt(targetUserId, 10));
            setUserStats(userData);
            setGlobalStats(null);
          } catch (err) {
            if (isStatsForbidden(err)) {
              setUserStats(null);
              setGlobalStats(null);
            } else {
              throw err;
            }
          }
        }
      } else {
        try {
          const agentData = await dashboardService.getGlobalStats();
          setGlobalStats(agentData);
        } catch (err) {
          if (isStatsForbidden(err)) {
            setGlobalStats(null);
          } else {
            throw err;
          }
        }
        setUserStats(null);
      }

      await fetchTransactions();
    } catch (err) {
      setError('Failed to fetch dashboard data');
      console.error('Error fetching dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredUsers = (): User[] => {
    let filteredUsers: User[] = [];
    const tokenUserId = parseInt(userId || '', 10);
    const localUserId = parseInt(localStorage.getItem('userId') || '', 10);
    const resolvedCurrentUserId = Number.isNaN(tokenUserId) ? localUserId : tokenUserId;
    const linkedToCurrentSuperAgent = (user: User): boolean => {
      const u = user as User & { parentId?: number | string };
      const createdBy = typeof user.createdById === 'number' ? user.createdById : NaN;
      const parentId =
        typeof u.parentId === 'number'
          ? u.parentId
          : typeof u.parentId === 'string'
            ? parseInt(u.parentId, 10)
            : NaN;
      return (
        !Number.isNaN(resolvedCurrentUserId) &&
        (createdBy === resolvedCurrentUserId || parentId === resolvedCurrentUserId)
      );
    };
    
    if (userRole === 'Admin') {
      filteredUsers = users; // Admin sees all users
    } else if (userRole === 'SuperAgent') {
      // SuperAgent sees self and only linked Agents.
      filteredUsers = users.filter((user) =>
        (resolvedCurrentUserId ? user.id === resolvedCurrentUserId : false) ||
        (user.role === 'Agent' && linkedToCurrentSuperAgent(user))
      );
      // Fallback if backend omits linkage fields: keep agents list visible.
      if (filteredUsers.length === 0) {
        filteredUsers = users.filter((user) => user.role === 'Agent');
      }
    } else {
      // Agent sees only self
      filteredUsers = users.filter((user) => (resolvedCurrentUserId ? user.id === resolvedCurrentUserId : false));
    }

    // Apply search filter
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      filteredUsers = filteredUsers.filter((user) =>
        (user.name || '').toLowerCase().includes(q) ||
        (user.email || '').toLowerCase().includes(q) ||
        String((user as Partial<User> & { username?: string }).username || '').toLowerCase().includes(q)
      );
    }

    return filteredUsers;
  };

  const getSelectedUserDisplay = (): string => {
    if (selectedUserId === 'global') {
      return '🌐 Global Statistics';
    }
    
    const user = users.find(u => u.id === parseInt(selectedUserId));
    if (user) {
      const displayName = user.name || (user as Partial<User> & { username?: string }).username || `User ${user.id}`;
      return `👤 ${displayName} (${user.role})`;
    }
    
    return 'Select User';
  };

  const formatDate = (timestamp: string): { date: string; time: string } => {
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) {
        return { date: 'Invalid Date', time: '' };
      }
      return {
        date: date.toLocaleDateString(),
        time: date.toLocaleTimeString()
      };
    } catch (error) {
      console.error('Error formatting date:', error);
      return { date: 'Invalid Date', time: '' };
    }
  };

  const handleUserSelect = (userId: string) => {
    setSelectedUserId(userId);
    setSearchTerm('');
    setShowDropdown(false);
  };

  const activeStats = globalStats || userStats;
  const merchantProfile = userRole === 'Agent' ? 'Merchant' : (userRole || 'Unknown');
  const filteredUsers = getFilteredUsers();
  const lastChecked = new Date().toLocaleTimeString();
  const recentActivity = transactions.slice(0, 5);
  const recentNotifications = transactions.slice(0, 6);
  const topSubUsers = [...(userStats?.subUsers || [])]
    .sort((a, b) => (b.stats.totalVolume || 0) - (a.stats.totalVolume || 0))
    .slice(0, 3);

  const goToTransactionsQueue = () => {
    navigate('/transactions', { state: { transactionsQueue: 'attention' as const } });
  };

  const refreshDashboard = () => {
    void fetchDashboardData();
  };

  return (
    <div className="min-h-screen px-4 py-6 homeBg sm:px-6 lg:px-8">
      <div className="mx-auto w-full max-w-[1440px] space-y-[40px]">
        {(userRole === 'Admin' || userRole === 'SuperAgent') && (
          <div className="rounded-[30px] relative border border-[#d9dde2] bg-[#f6f6f6] p-4 lg:p-[30px] shadow-lg blurLayerInteractiveInteractive" >
            <div className="max-w-2xl " ref={dropdownRef}>
              <p className="mb-3 uppercase   text-[#4A4A4A]">View scope</p>
              <p className="mb-2 text-sm md:text-[24px] font-semibold text-slate-900">Choose which portfolio you want to follow.</p>
              <p className="mb-3 text-sm md:text-base text-slate-600">Search by account name or email, or switch back to the full overview.</p>
              <div className="relative">
                <input
                  type="text"
                  placeholder={loadingUsers ? 'Loading users...' : 'Search accounts by name or email'}
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setShowDropdown(true);
                  }}
                  onFocus={() => setShowDropdown(true)}
                  disabled={loadingUsers}
                  className="w-full rounded-xl border border-[#e0e3e7] bg-[#eceff2] px-4 md:px-[30px] py-3   text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#2297D1]/30"
                />
                <button
                  type="button"
                  className="absolute right-8 top-1/2 -translate-y-1/2 text-xs font-semibold text-sky-600"
                  onClick={() => setShowDropdown(!showDropdown)}
                >
                  {showDropdown ? 'Hide' : 'Show'}
                </button>
                {showDropdown && !loadingUsers && (
                  <div className="absolute z-50 mt-1 max-h-56 w-full overflow-y-auto rounded-xl border border-[#d9dde2] bg-white shadow-lg">
                    {userRole === 'Admin' && (
                      <button
                        type="button"
                        onClick={() => handleUserSelect('global')}
                        className={cn(
                          'w-full border-b border-[#eef1f4] px-4 py-3 text-left text-sm',
                          selectedUserId === 'global' ? 'bg-[#2297D1] text-white' : 'bg-white text-slate-700 hover:bg-[#f1f5f8]'
                        )}
                      >
                        Global Statistics
                      </button>
                    )}
                    {filteredUsers.length > 0 ? (
                      filteredUsers.map((user) => (
                        <button
                          key={user.id}
                          type="button"
                          onClick={() => handleUserSelect(user.id.toString())}
                          className={cn(
                            'w-full border-b border-[#eef1f4] px-4 py-3 text-left',
                            selectedUserId === user.id.toString() ? 'bg-[#2297D1] text-white' : 'bg-white text-slate-700 hover:bg-[#f1f5f8]'
                          )}
                        >
                          <p className="mb-0 text-sm font-semibold">{user.name || (user as Partial<User> & { username?: string }).username || `User ${user.id}`} ({user.role})</p>
                          <p className={cn('mb-0 text-xs', selectedUserId === user.id.toString() ? 'text-white/90' : 'text-slate-500')}>{user.email}</p>
                        </button>
                      ))
                    ) : (
                      <p className="mb-0 px-4 py-3 text-sm text-slate-400">No users found</p>
                    )}
                  </div>
                )}
              </div>
              {selectedUserId && (
                <div className="mt-3 rounded-xl border border-[#e0e3e7] bg-[#eceff2] px-4 py-3 text-sm text-slate-700">
                  <span className="text-slate-500">Current view: </span>{getSelectedUserDisplay()}
                </div>
              )}
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex min-h-40 items-center justify-center rounded-3xl border border-[#d9dde2] bg-[#f5f6f7]">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#2297D1] border-t-transparent" />
          </div>
        ) : error ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            <span>{error}</span>
            <button type="button" onClick={() => fetchDashboardData()} className="ml-3 rounded-lg bg-red-600 px-3 py-1.5 text-white">Retry</button>
          </div>
        ) : (
          <>
            {userRole === 'Agent' ? (
              <>
                <SurfaceCard className="relative z-0   blurLayerInteractiveInteractive">

                  <p className="mb-2 text-base font-semibold uppercase text-[#4A4A4A]">Live operations cockpit</p>
                  <h1 className="max-w-4xl text-3xl font-semibold tracking-tight text-slate-900 sm:text-5xl">
                    See collection flow, queue pressure, and settlement health in one place.
                  </h1>
                  <p className="mt-3 text-sm text-slate-600">Master your own portfolio and keep queues moving without context switching.</p>
                  <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    <StatTile label="Current scope" value="Choose a view" />
                    <StatTile label="Operator profile" value="Agent" />
                    <StatTile label="Realtime state" value={`last checked ${lastChecked}`} />
                    <StatTile label="Alerting" value="browser alerts off | payment tone on" />
                    <StatTile label="Queues needs review" value={activeStats?.totalPending || 0} />
                    <StatTile label="Settled volume" value={activeStats?.totalVolume || 0} />
                  </div>
                </SurfaceCard>

                <SurfaceCard className='w-full  '>
                <p className="mb-2 text-base uppercase  text-[#4A4A4A]">Live alerts</p>
                <p className="mb-[15px] text-2xl font-semibold text-slate-900">Decide how the dashboard gets your attention.</p>
                <p className="mb-[15px] text-base text-slate-600">Keep live alerts lightweight so you can stay focused on payments that need follow-up without leaving this screen.</p>
                <div className="mb-3 flex items-center gap-2 text-basefont-semibold text-slate-900"><span className=' p-[18px] border-[1px] rounded-[16px]'>Turn on browser alerts</span><span className="p-[18px] border-[1px] rounded-[16px] text-[#008834]">Payment tone: on</span></div>
                <div className="rounded-xl border border-[#e0e3e7] bg-[#eceff2] px-4 py-3 text-sm"><p className="mb-1 text-xs text-slate-500">Current live state</p><p className="mb-0 font-semibold text-slate-900">browser alerts off | payment tone on</p></div>
              </SurfaceCard> 

{/* metrix card  issue one */}
                <div className="grid gap-4 grid-cols-[repeat(auto-fit,minmax(140px,1fr))]">
                  <div className="">
                    <MetricStatCard label="Total Issued" value={activeStats?.totalIssued || 0} accent="blue" />
                  </div>
                  
                  <div className="">
                    <MetricStatCard label="Completed" value={activeStats?.totalCompleted || 0} accent="lightgray" />
                  </div>
                  <div className="">
                    <MetricStatCard label="Pending" value={activeStats?.totalPending || 0} accent="mediumgray" />
                  </div>
                  <div className="">
                    <MetricStatCard label="Failed" value={activeStats?.totalFailed || 0} accent="darkgray" />
                  </div>
                  <div className="">
                    <MetricStatCard
                      label="Settled volume"
                      value={activeStats?.totalVolume || 0}
                      accent="blue"
                      volumeIcon
                    />
                  </div>
                  <div className="">
                    <MetricStatCard label="Sub Users" value={userStats?.subUsers?.length || 0} accent="mediumgray" />
                  </div>
                </div>

                  <SurfaceCard className='!p-0'>
                <div className="mb-3 flex items-center justify-between border-b-[1px] py-[18px] px-[30px]">
                    <h3 className="mb-0 text-2xl font-semibold text-slate-900">Ops Priority Queue</h3>
                    <p className="mb-0 text-sm text-slate-500">Updated: {lastChecked}</p>
                 
              </div>





                  <div className="grid gap-4 md:grid-cols-3 p-[30px]">
                    <div className="rounded-2xl bg-[#F0F0F0] overflow-hidden" >
                      <div className="p-4" style={{ borderLeft: '6px solid #929698' }}>
                      <p className="mb-1 text-[10px] sm:text-base uppercase font-semibold text-[#4A4A4A]">Open Exceptions</p>
                      <p className="mb-1 text-2xl font-semibold text-slate-900">{activeStats?.totalPending || 0}</p>
                      <p className="mb-3 text-xs text-slate-500">Oldest age: {transactions[0] ? formatDate(transactions[0].timestamp).time : '--'}</p>
                      <button
                        type="button"
                        className="rounded-xl border border-[#d2d8dd] p-[18px] text-xs sm:text-base font-semibold text-slate-700"
                        onClick={goToTransactionsQueue}
                      >
                        Open Queue
                      </button>
                      </div>
                    </div>


                    <div className="rounded-2xl bg-[#F0F0F0] overflow-hidden"  >
                      <div className="p-4 " style={{ borderLeft: '6px solid #929698' }}>
                      <p className="mb-1 text-[10px] sm:text-base uppercase font-semibold text-[#4A4A4A]">Webhook Retries</p>
                      <p className="mb-1 text-2xl font-semibold text-slate-900">{Math.max(0, (activeStats?.totalPending || 0) - 1)}</p>
                      <p className="mb-3 text-xs text-slate-500">Webhook retry older than 1h should trigger manual review.</p>
                      <button
                        type="button"
                        className="rounded-xl border border-[#d2d8dd] p-[18px] text-xs sm:text-base font-semibold text-slate-700"
                        onClick={goToTransactionsQueue}
                      >
                        Open Queue
                      </button>
                      </div>
                    </div>



                    <div className="rounded-2xl bg-[#F0F0F0] overflow-hidden"  >
                      <div className="p-4 " style={{ borderLeft: '6px solid #929698' }}>
                      <p className="mb-1 text-[10px] sm:text-base uppercase font-semibold text-sky-600">Unresolved Underpayments</p>
                      <p className="mb-1 text-2xl font-semibold text-slate-900">{activeStats?.totalFailed || 0}</p>
                      <p className="mb-3 text-xs text-slate-500">Oldest age: {transactions[1] ? formatDate(transactions[1].timestamp).time : '--'}</p>
                      <button
                        type="button"
                        className="rounded-xl border border-[#d2d8dd] p-[18px] text-xs sm:text-base font-semibold text-slate-700"
                        onClick={goToTransactionsQueue}
                      >
                        Open Queue
                      </button>
                      </div>
                    </div>
                  </div>


                  <div className='px-[30px]'>
                    <p>
                    Smaller queues stay here so you can still spot overdue follow-ups without turning the dashboard into a wall of cards.
                  </p>
                  <div className="py-[30px] flex flex-wrap gap-2">
                    {[
                      { label: 'Overdue Cases', value: 0, msg: 'No item waiting', active: true },
                      { label: 'Esclated', value: 0, msg: 'No item waiting', active: false },
                      { label: 'Assigned to Me', value: 0, msg: 'No item waiting', active: false },
                      { label: 'Unassigned', value: 0, msg: 'No item waiting', active: false },
                      { label: 'Reminder Due', value: 0, msg: 'No item waiting', active: false },
                      { label: 'Esclation Due', value: 0, msg: 'No item waiting', active: false },
                    ].map((item) => (
                      <div
                        key={item.label}
                        className={`flex-1 min-w-[120px] rounded-xl p-3 flex flex-col gap-2 ${item.active ? 'bg-[#2297D1]/10 border-2 border-[#2297D1]' : ''}`}
                      >
                        <p className={`text-base uppercase font-semibold tracking-wide ${item.active ? 'text-[#2297D1]' : 'text-[#4A4A4A]'}`}>{item.label}</p>
                        <p className="text-base font-semibold text-slate-900">{item.value}</p>
                        <p className="text-[10px] text-slate-400">{item.msg}</p>
                        <button
                          type="button"
                          className="text-[10px] font-semibold text-[#2297D1] text-left"
                          onClick={goToTransactionsQueue}
                        >
                          Open Queue
                        </button>
                      </div>
                    ))}
                  </div>
                  </div>
                </SurfaceCard>

                <div className="flex flex-wrap gap-[40px] xl:flex-nowrap  *:h-[195px] ">
              <SurfaceCard className="w-full xl:flex-1 !p-0 overflow-hidden">
                <div className=" border-b-[1px] py-[18px] px-[30px]">
                  <h3 className="mb-0 text-2xl font-semibold text-slate-900">Recent Activity</h3>
                </div>
                <div className="overflow-hidden px-[30px] pb-[24px] pt-[24px]">
                  {recentActivity.length > 0 ? (
                    <ul className="theme-scrollbar max-h-[100px] space-y-2 pr-2">
                      {recentActivity.map((tx) => (
                        <li key={tx.id} className="text-sm lg:text-base text-slate-700">
                          <span className="font-semibold">#{tx.id}</span> {tx.status} — {formatDate(tx.timestamp).date}, {formatDate(tx.timestamp).time}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="mb-0 text-sm lg:text-base text-slate-500">No recent activity yet.</p>
                  )}
                </div>
              </SurfaceCard>
              <SurfaceCard className='w-full xl:w-[560px] !p-0 overflow-hidden'>
                <div className="mb-2 flex items-center justify-between border-b-[1px] py-[13px] px-[30px]">
                  <h3 className="mb-0 text-2xl font-semibold text-slate-900">Recent Notifications</h3>
                  <div className="flex flex-wrap sm:flex-nowrap justify-end gap-[10px] *:h-[37px] *:border-[1px] *:px-[13px] *:rounded-[6px]">
                    <button
                      type="button"
                      className="text-sm text-slate-700 underline-offset-2 hover:text-[#2297D1] hover:underline"
                      onClick={refreshDashboard}
                    >
                      Refresh
                    </button>
                    <button
                      type="button"
                      className="text-sm text-slate-700 underline-offset-2 hover:text-[#2297D1] hover:underline"
                      onClick={() => navigate('/transactions')}
                    >
                      View
                    </button>
                  </div>
                </div>
                <div className="overflow-hidden px-[30px] pb-[24px] pt-[24px]">
                  {recentNotifications.length > 0 ? (
                    <ul className="theme-scrollbar max-h-[100px] space-y-2 pr-2">
                      {recentNotifications.map((tx) => (
                        <li key={tx.id}>
                          <button
                            type="button"
                            className="text-left text-sm lg:text-base text-slate-700 underline-offset-2 hover:text-[#2297D1] hover:underline"
                            onClick={() => navigate('/transactions', { state: { openTransactionId: tx.id } })}
                          >
                            Transaction #{tx.id} — {tx.status}
                            {tx.fiatAmount ? ` (${tx.fiatAmount} ${tx.fiatCurrency})` : ''}
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="mb-0 text-sm lg:text-base text-slate-500">No transaction notifications yet.</p>
                  )}
                </div>
              </SurfaceCard>
            </div>

                <SurfaceCard className='!p-0'>
                <div className="mb-3 flex items-center justify-between border-b-[1px] py-[18px] px-[12px] sm:px-[30px]">
                <div><h3 className="mb-0 text-2xl font-semibold text-slate-900">Recent payments</h3><p className="mb-0 text-xs text-slate-500">Recent payments across your portfolio</p></div>
                <button
                  type="button"
                  className="mb-0 text-base !border border-[#00000038] rounded-[16px] p-[8px] sm:p-[18px] font-medium text-[#313131]"
                  onClick={() => navigate('/transactions')}
                  >
                  Open full review workspace
                </button>
              </div>
                  <div className="flex items-start justify-between gap-3 p-4">
                   <div className="rounded-2xl border border-[#e2e6ea] bg-[#f0f2f4] p-4  w-full">
                    {transactions.length > 0 ? transactions.map((tx) => (
                      <div key={tx.id} className="rounded-2xl border border-[#e2e6ea] bg-[#f0f2f4] p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="mb-1 text-sm font-semibold text-slate-900">Transaction #{tx.id}</p>
                            <p className="mb-2 text-xs text-slate-500">{tx.userName || `User ${tx.userId}`}</p>
                            <p className="mb-1 text-xl font-semibold text-slate-900">{tx.fiatAmount ? `${tx.fiatAmount} ${tx.fiatCurrency}` : `${tx.amount} ${tx.currency}`}</p>
                            {tx.fiatAmount && <p className="mb-0 text-xs text-slate-500">{tx.amount} {tx.currency}</p>}
                          </div>
                          <button
                            type="button"
                            className={cn(
                              'rounded-md px-2.5 py-1 text-xs font-semibold text-white transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
                              tx.status === 'Completed' ? 'bg-emerald-500 focus-visible:ring-emerald-500' :
                              tx.status === 'Pending' ? 'bg-amber-500 focus-visible:ring-amber-500' :
                              tx.status === 'Issued' ? 'bg-[#2297D1] focus-visible:ring-[#2297D1]' : 'bg-red-500 focus-visible:ring-red-500'
                            )}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate('/transactions', { state: { openTransactionId: tx.id } });
                            }}
                          >
                            {tx.status}
                          </button>
                        </div>
                        <p className="mb-0 mt-3 text-right text-xs text-slate-500">{formatDate(tx.timestamp).date}, {formatDate(tx.timestamp).time}</p>
                      </div>
                    )) : (
                      <p className="mb-0 text-sm text-slate-500">No recent transactions found</p>
                    )}
                  </div>
                    </div>
                </SurfaceCard>
              </>
            ) : (
            <>
            <SurfaceCard className=" blurLayerInteractiveInteractive">
              <p className="mb-2 text-base   uppercase text-[#4A4A4A]">Live operations cockpit</p>
              <h1 className="max-w-4xl text-[40px] font-semibold tracking-tight text-slate-900 sm:text-5xl">
                See collection flow, queue pressure, and settlement health in one place.
              </h1>
              <p className="mt-3 text-sm text-slate-600">Monitor your own portfolio, your agents, and the queues that still need intervention.</p>
              <div className="mt-6 grid gap-3 lg:gap-5 sm:grid-cols-2 lg:grid-cols-3">
                <StatTile label="Current scope" value={selectedUserId ? getSelectedUserDisplay() : '-'} />
                <StatTile label="Operator profile" value={merchantProfile} />
                <StatTile label="Realtime state" value={`last checked ${lastChecked}`} />
                <StatTile label="Alerting" value="browser alerts off | payment tone on" />
                <StatTile label="Queues needing review" value={activeStats?.totalPending || 0} />
                <StatTile label="Settled volume" value={activeStats?.totalVolume || 0} />
              </div>
            </SurfaceCard>

            <div className="flex gap-[40px] flex-wrap xl:flex-nowrap">
              <SurfaceCard className="w-full xl:flex-1">
                <p className="mb-2 text-base uppercase  text-[#4A4A4A]">Current focus</p>
                <p className="mb-2 text-2xl font-semibold text-slate-900">Quick status of your selected portfolio.</p>
                <p className="mb-3 text-base text-slate-600">Use the top View scope control to change account context. This panel updates automatically with your current selection.</p>
                <p className="mb-4 text-sm text-slate-400">Refreshes every 15 seconds | Last checked {lastChecked}</p>
                <div className="rounded-xl border border-[#e0e3e7] bg-[#eceff2] px-4 py-3 text-sm">
                  <p className="mb-1 text-xs text-slate-500">You are currently reviewing</p>
                  <p className="mb-0 font-semibold text-slate-900">{selectedUserId ? getSelectedUserDisplay() : '-'}</p>
                </div>
              </SurfaceCard>
              <SurfaceCard className='w-full xl:w-[560px]'>
                <p className="mb-2 text-base uppercase  text-[#4A4A4A]">Live alerts</p>
                <p className="mb-[15px] text-2xl font-semibold text-slate-900">Decide how the dashboard gets your attention.</p>
                <p className="mb-[15px] text-base text-slate-600">Keep live alerts lightweight so you can stay focused on payments that need follow-up without leaving this screen.</p>
                <div className="mb-3 flex items-center gap-2 text-basefont-semibold text-slate-900"><span className=' p-[18px] border-[1px] rounded-[16px]'>Turn on browser alerts</span><span className="p-[18px] border-[1px] rounded-[16px] text-[#008834]">Payment tone: on</span></div>
                <div className="rounded-xl border border-[#e0e3e7] bg-[#eceff2] px-4 py-3 text-sm"><p className="mb-1 text-xs text-slate-500">Current live state</p><p className="mb-0 font-semibold text-slate-900">browser alerts off | payment tone on</p></div>
              </SurfaceCard> 
            </div>

              {/* <div className="mb-2 flex items-center justify-between px-1">
                <div>
                  <h3 className="mb-0 text-xl font-semibold text-slate-900">Top account snapshots</h3>
                  <p className="mb-0 text-xs text-slate-500">The busiest accounts in the current view, ranked by settled volume.</p>
                </div>
              </div> */}
              <div className="rounded-2xl">
                {topSubUsers.length > 0 ? (
                  topSubUsers.map((subUser, index) => {
                    const subUserData = users.find((user) => user.id === subUser.userId);
                    return (
                      <div key={subUser.userId} className="mb-4 last:mb-0">
                        {/* <div className="mb-3 flex items-center justify-between">
                          <div>
                            <p className="mb-0 text-sm font-semibold text-sky-600">{subUserData ? subUserData.name : `User ${subUser.userId}`}</p>
                            <p className="mb-0 text-xs text-slate-500">{subUserData ? `(${subUserData.role})` : `ID: ${subUser.userId}`}</p>
                          </div>
                          <span className="rounded-full bg-sky-100 px-3 py-1 text-xs font-semibold text-sky-700">
                            Rank #{index + 1}
                          </span>
                        </div> */}
                        <div className="grid gap-4 grid-cols-[repeat(auto-fit,minmax(150px,1fr))]">
                          <MetricStatCard label="Issued" value={subUser.stats.totalIssued} accent="blue" />
                          <MetricStatCard label="Completed" value={subUser.stats.totalCompleted} accent="lightgray" />
                          <MetricStatCard label="Pending" value={subUser.stats.totalPending} accent="mediumgray" />
                          <MetricStatCard label="Failed" value={subUser.stats.totalFailed} accent="darkgray" />
                          <MetricStatCard label="Settled volume" value={subUser.stats.totalVolume} accent="blue" volumeIcon />
                          
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="grid gap-4 grid-cols-[repeat(auto-fit,minmax(150px,1fr))]">
                    <MetricStatCard label="Issued" value={activeStats?.totalIssued || 0} accent="blue" />
                    <MetricStatCard label="Completed" value={activeStats?.totalCompleted || 0} accent="lightgray" />
                    <MetricStatCard label="Pending" value={activeStats?.totalPending || 0} accent="mediumgray" />
                    <MetricStatCard label="Failed" value={activeStats?.totalFailed || 0} accent="darkgray" />
                    <MetricStatCard label="Settled volume" value={activeStats?.totalVolume || 0} accent="blue" volumeIcon />
                    
                          <MetricStatCard label="Sub User" value={userStats?.subUsers?.length || 0} accent="darkgray"  />
                  </div>
                )}
              </div>

            <div className="flex flex-wrap gap-[40px] xl:flex-nowrap  *:h-[195px] ">
              <SurfaceCard className="w-full xl:flex-1 !p-0 overflow-hidden">
                <div className=" border-b-[1px] py-[18px] px-[30px]">
                  <h3 className="mb-0 text-2xl font-semibold text-slate-900">Recent Activity</h3>
                </div>
                <div className="overflow-hidden px-[30px] pb-[24px] pt-[24px]">
                  {recentActivity.length > 0 ? (
                    <ul className="theme-scrollbar max-h-[100px] space-y-2 pr-2">
                      {recentActivity.map((tx) => (
                        <li key={tx.id} className="text-sm lg:text-base text-slate-700">
                          <span className="font-semibold">#{tx.id}</span> {tx.status} — {formatDate(tx.timestamp).date}, {formatDate(tx.timestamp).time}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="mb-0 text-sm lg:text-base text-slate-500">No recent activity yet.</p>
                  )}
                </div>
              </SurfaceCard>
              <SurfaceCard className='w-full xl:w-[560px] !p-0 overflow-hidden'>
                <div className="mb-2 flex items-center justify-between border-b-[1px] py-[13px] px-[30px]">
                  <h3 className="mb-0 text-2xl font-semibold text-slate-900">Recent Notifications</h3>
                  <div className="flex flex-wrap sm:flex-nowrap justify-end gap-[10px] *:h-[37px] *:border-[1px] *:px-[13px] *:rounded-[6px]">
                    <button
                      type="button"
                      className="text-sm text-slate-700 underline-offset-2 hover:text-[#2297D1] hover:underline"
                      onClick={refreshDashboard}
                    >
                      Refresh
                    </button>
                    <button
                      type="button"
                      className="text-sm text-slate-700 underline-offset-2 hover:text-[#2297D1] hover:underline"
                      onClick={() => navigate('/transactions')}
                    >
                      View
                    </button>
                  </div>
                </div>
                <div className="overflow-hidden px-[30px] pb-[24px] pt-[24px]">
                  {recentNotifications.length > 0 ? (
                    <ul className="theme-scrollbar max-h-[100px] space-y-2 pr-2">
                      {recentNotifications.map((tx) => (
                        <li key={tx.id}>
                          <button
                            type="button"
                            className="text-left text-sm lg:text-base text-slate-700 underline-offset-2 hover:text-[#2297D1] hover:underline"
                            onClick={() => navigate('/transactions', { state: { openTransactionId: tx.id } })}
                          >
                            Transaction #{tx.id} — {tx.status}
                            {tx.fiatAmount ? ` (${tx.fiatAmount} ${tx.fiatCurrency})` : ''}
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="mb-0 text-sm lg:text-base text-slate-500">No transaction notifications yet.</p>
                  )}
                </div>
              </SurfaceCard>
            </div>

            <SurfaceCard className='!p-0'>
                <div className="mb-3 flex items-center justify-between border-b-[1px] py-[18px] px-[12px] sm:px-[30px]">
                <div><h3 className="mb-0 text-2xl font-semibold text-slate-900">Recent payments</h3><p className="mb-0 text-xs text-slate-500">Recent payments across your portfolio</p></div>
                <button
                  type="button"
                  className="mb-0 text-base !border border-[#00000038] rounded-[16px] p-[10px] sm:p-[18px] font-medium text-[#313131]"
                  onClick={() => navigate('/transactions')}
                  >
                  Open full review workspace
                </button>
              </div>
                  <div className="flex items-start justify-between gap-3 p-4">
                   <div className="rounded-2xl border border-[#e2e6ea] bg-[#f0f2f4] p-4  w-full">
                    {transactions.length > 0 ? transactions.map((tx) => (
                      <div key={tx.id} className="rounded-2xl border border-[#e2e6ea] bg-[#f0f2f4] p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="mb-1 text-base font-semibold text-slate-900">Transaction #{tx.id}</p>
                            <p className="mb-2 text-sm text-slate-500">{tx.userName || `User ${tx.userId}`}</p>
                            <p className="mb-1 text-base font-semibold text-[#2297D1]">{tx.fiatAmount ? `${tx.fiatAmount} ${tx.fiatCurrency}` : `${tx.amount} ${tx.currency}`}</p>
                            {tx.fiatAmount && <p className="mb-0 text-sm text-slate-500">{tx.amount} {tx.currency}</p>}
                          </div>
                          <button
                            type="button"
                            className={cn(
                              'rounded-md px-2.5 py-1 md:text-sm text-base font-semibold text-white transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
                              tx.status === 'Completed' ? 'bg-emerald-500 focus-visible:ring-emerald-500' :
                              tx.status === 'Pending' ? 'bg-amber-500 focus-visible:ring-amber-500' :
                              tx.status === 'Issued' ? 'bg-[#2297D1] focus-visible:ring-[#2297D1]' : 'bg-red-500 focus-visible:ring-red-500'
                            )}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate('/transactions', { state: { openTransactionId: tx.id } });
                            }}
                          >
                            {tx.status}
                          </button>
                        </div>
                        <p className="mb-0 mt-3 text-right text-sm text-slate-500">{formatDate(tx.timestamp).date}, {formatDate(tx.timestamp).time}</p>
                      </div>
                    )) : (
                      <p className="mb-0 text-sm text-slate-500">No recent transactions found</p>
                    )}
                  </div>
                    </div>
            </SurfaceCard>
            </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
